import { isNavigationFailure, type RouteLocationRaw } from 'vue-router';

interface CatalogScrollRestoration {
  targetFullPath: string;
  top: number;
}

export function useCatalogScrollRestoration() {
  const router = useRouter();
  const restoration = useState<CatalogScrollRestoration | null>('catalog-scroll-restoration', () => null);
  const isCatalogScrollRestorationActive = useState('catalog-scroll-restoration-active', () => false);
  const isCatalogScrollRestorationPending = computed(() => restoration.value !== null);

  function clearRestoration(targetFullPath: string) {
    if (restoration.value?.targetFullPath === targetFullPath) {
      restoration.value = null;
      isCatalogScrollRestorationActive.value = false;
    }
  }

  async function navigateToCatalogRoutePreservingScroll(target: RouteLocationRaw) {
    if (import.meta.server) {
      return navigateTo(target);
    }

    const targetFullPath = router.resolve(target).fullPath;

    restoration.value = {
      targetFullPath,
      top: window.scrollY,
    };
    isCatalogScrollRestorationActive.value = false;

    const removeScrollGuard = router.beforeEach((to) => {
      if (to.fullPath === targetFullPath) {
        to.meta.scrollToTop = false;
      }
    });

    try {
      const navigationFailure = await navigateTo(target);

      if (isNavigationFailure(navigationFailure)) {
        clearRestoration(targetFullPath);
      }

      return navigationFailure;
    } catch (error) {
      clearRestoration(targetFullPath);
      throw error;
    } finally {
      removeScrollGuard();
    }
  }

  function consumeCatalogScrollPosition(fullPath: string): number | null {
    const pendingRestoration = restoration.value;

    if (pendingRestoration?.targetFullPath !== fullPath) {
      restoration.value = null;
      isCatalogScrollRestorationActive.value = false;

      return null;
    }

    return pendingRestoration.top;
  }

  function beginCatalogScrollRestoration(fullPath: string) {
    if (restoration.value?.targetFullPath === fullPath) {
      isCatalogScrollRestorationActive.value = true;
    }
  }

  function finishCatalogScrollRestoration(fullPath: string) {
    clearRestoration(fullPath);
  }

  return {
    beginCatalogScrollRestoration,
    consumeCatalogScrollPosition,
    finishCatalogScrollRestoration,
    isCatalogScrollRestorationActive,
    isCatalogScrollRestorationPending,
    navigateToCatalogRoutePreservingScroll,
  };
}
